// Client-side storage utilities
import { User, Message } from './types';

const USERS_KEY = 'users';
const CURRENT_USER_KEY = 'current_user';
const MESSAGES_KEY = 'messages';

// Get all users from localStorage
export const getAllUsers = (): User[] => {
  try {
    const usersJson = localStorage.getItem(USERS_KEY);
    return usersJson ? JSON.parse(usersJson) : [];
  } catch (error) {
    console.error('Error getting users:', error);
    return [];
  }
};

// Save users to localStorage
export const saveUsers = (users: User[]): void => {
  try {
    localStorage.setItem(USERS_KEY, JSON.stringify(users));
    // Dispatch storage event for cross-tab synchronization
    window.dispatchEvent(new StorageEvent('storage', {
      key: USERS_KEY,
      newValue: JSON.stringify(users)
    }));
  } catch (error) {
    console.error('Error saving users:', error);
  }
};

// Get current user from localStorage
export const getCurrentUser = (): User | null => {
  try {
    const userJson = localStorage.getItem(CURRENT_USER_KEY);
    return userJson ? JSON.parse(userJson) : null;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

// Save current user to localStorage
export const saveCurrentUser = (user: User): void => {
  try {
    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
    // Dispatch storage event for cross-tab synchronization
    window.dispatchEvent(new StorageEvent('storage', {
      key: CURRENT_USER_KEY,
      newValue: JSON.stringify(user)
    }));
  } catch (error) {
    console.error('Error saving current user:', error);
  }
};

// Find user by email (case-insensitive)
export const findUserByEmail = (email: string): User | null => {
  const users = getAllUsers();
  const normalizedEmail = email.toLowerCase().trim();
  return users.find(u => u.email.toLowerCase().trim() === normalizedEmail) || null;
};

// Add new user
export const addUser = (user: User): void => {
  const users = getAllUsers();
  users.push(user);
  saveUsers(users);
};

// Update user
export const updateUser = async (updatedUser: User): Promise<void> => {
  const users = getAllUsers();
  const index = users.findIndex(u => u.id === updatedUser.id);
  if (index !== -1) {
    users[index] = updatedUser;
    saveUsers(users);
    
    // Update current user if it's the same user
    const currentUser = getCurrentUser();
    if (currentUser && currentUser.id === updatedUser.id) {
      saveCurrentUser(updatedUser);
    }
  }
};

// Delete user
export const deleteUser = (userId: string): void => {
  const users = getAllUsers();
  const filteredUsers = users.filter(u => u.id !== userId);
  saveUsers(filteredUsers);
  
  // Clear current user if it's the deleted user
  const currentUser = getCurrentUser();
  if (currentUser && currentUser.id === userId) {
    localStorage.removeItem(CURRENT_USER_KEY);
  }
};

// Clear all data (for testing/development)
export const clearAllData = (): void => {
  localStorage.removeItem(USERS_KEY);
  localStorage.removeItem(CURRENT_USER_KEY);
  localStorage.removeItem(MESSAGES_KEY);
};

// Query function for message operations
export async function query<T>(sql: string, params: any[] = []): Promise<T> {
  try {
    // Get messages from localStorage
    const messages = JSON.parse(localStorage.getItem(MESSAGES_KEY) || '[]');
    
    // Parse the SQL-like query to determine the operation
    if (sql.toLowerCase().includes('select')) {
      if (sql.includes('COUNT(*)')) {
        // Handle count query
        const unreadCount = messages.filter((m: Message) => !m.readAt && !m.isArchived).length;
        return [{ count: unreadCount }] as unknown as T;
      }
      // Handle select query
      return messages as unknown as T;
    }
    
    else if (sql.toLowerCase().includes('insert')) {
      // Handle insert
      const newMessage = {
        id: `msg-${Date.now()}`,
        senderName: params[0],
        senderEmail: params[1],
        content: params[2],
        createdAt: new Date().toISOString(),
        isArchived: false
      };
      messages.push(newMessage);
      localStorage.setItem(MESSAGES_KEY, JSON.stringify(messages));
      return { insertId: messages.length } as unknown as T;
    }
    
    else if (sql.toLowerCase().includes('update')) {
      // Handle update
      const updatedMessages = messages.map((m: Message) => {
        if (m.id === params[params.length - 1]) {
          if (sql.includes('readAt')) {
            return { ...m, readAt: new Date().toISOString() };
          }
          if (sql.includes('reply')) {
            return { ...m, reply: params[0], repliedAt: new Date().toISOString() };
          }
          if (sql.includes('isArchived')) {
            return { ...m, isArchived: true };
          }
        }
        return m;
      });
      localStorage.setItem(MESSAGES_KEY, JSON.stringify(updatedMessages));
      return null as unknown as T;
    }
    
    else if (sql.toLowerCase().includes('delete')) {
      // Handle delete
      const filteredMessages = messages.filter((m: Message) => m.id !== params[0]);
      localStorage.setItem(MESSAGES_KEY, JSON.stringify(filteredMessages));
      return null as unknown as T;
    }

    throw new Error('Unsupported operation');
  } catch (error) {
    console.error('Query error:', error);
    throw error;
  }
}

// Transaction function (simplified for localStorage)
export async function transaction<T>(
  callback: (connection: any) => Promise<T>
): Promise<T> {
  try {
    // Execute the callback with a mock connection
    return await callback({});
  } catch (error) {
    console.error('Transaction error:', error);
    throw error;
  }
}